import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { adresseCanonique, estUneRouteDuSite, urlCanonique } from '@azoya-spa/contracts'
import { metadonneesDesPages, TITRE_INTROUVABLE } from './pages'

/*
 * Pose dans le `<head>` les métadonnées de la page courante (issue #141).
 *
 * Monté une seule fois, dans App, comme `usePageviews` : il lit la route et
 * n'attend rien des pages. Le faire appeler par chacune d'elles aurait
 * demandé à chaque nouvelle page d'y penser — et c'est exactement ce qui
 * n'était pas arrivé jusqu'ici, où huit `document.title` cohabitaient sans
 * qu'aucune page ne déclare de description.
 *
 * LIMITE CONNUE, et elle est importante. Ces balises sont écrites par
 * JavaScript, après le chargement. Google rend le JavaScript et les voit ;
 * les robots d'aperçu de Facebook, WhatsApp, LinkedIn et les crawlers de LLM,
 * non : ils lisent le HTML brut. Pour eux, seules comptent les valeurs
 * inscrites en dur dans `public/index.html`, qui décrivent le site dans son
 * ensemble. Un partage de la page d'accueil est donc correct, celui d'une
 * page intérieure affiche la description générale du site. Lever cette limite
 * demande le prérendu de l'issue #149 — c'est là que ce socle prend tout son
 * sens, pas ici.
 */

/** Crée la balise si elle manque, la réutilise sinon. */
function baliseUnique(selecteur, creer) {
  const existante = document.head.querySelector(selecteur)
  if (existante) return existante

  const nouvelle = creer()
  document.head.appendChild(nouvelle)
  return nouvelle
}

/**
 * Une valeur nulle retire la balise plutôt que d'écrire « null » dedans.
 *
 * Le cas se produit pour `og:url` sur une adresse qui répond 404 : il n'y a
 * pas d'URL canonique à annoncer, et un `content="null"` serait lu comme une
 * valeur par les robots d'aperçu.
 */
function poserMeta(attribut, nom, valeur) {
  if (valeur == null) {
    document.head.querySelector(`meta[${attribut}="${nom}"]`)?.remove()
    return
  }

  const balise = baliseUnique(`meta[${attribut}="${nom}"]`, () => {
    const creee = document.createElement('meta')
    creee.setAttribute(attribut, nom)
    return creee
  })
  balise.setAttribute('content', valeur)
}

function poserCanonique(url) {
  const existante = document.head.querySelector('link[rel="canonical"]')

  // Pas d'URL : la page n'est pas indexable. La balise déjà posée par une
  // page précédente doit disparaître, sinon la 404 hérite de la canonique de
  // la page d'où l'on vient et invite les moteurs à indexer une adresse qui
  // répond 404.
  if (!url) {
    existante?.remove()
    return
  }

  const balise =
    existante ??
    baliseUnique('link[rel="canonical"]', () => {
      const creee = document.createElement('link')
      creee.setAttribute('rel', 'canonical')
      return creee
    })
  balise.setAttribute('href', url)
}

export function useMetadonnees() {
  const { pathname } = useLocation()

  useEffect(() => {
    const connue = estUneRouteDuSite(pathname)
    const metadonnees = connue ? metadonneesDesPages[adresseCanonique(pathname)] : undefined

    // Une route déclarée mais sans texte — `/team`, qui rend la page
    // « introuvable » — se comporte ici comme une adresse inconnue.
    //
    // Les balises sont RÉÉCRITES, pas seulement laissées de côté : arrivée
    // ici depuis une autre page, la 404 héritait sinon de ses balises. Le
    // titre de l'onglet annonçait « Page introuvable » pendant que `og:title`
    // et la description décrivaient toujours la page précédente — un partage
    // de 404 présentait donc une page sans rapport. La description repart sur
    // celle du site, la seule qui reste vraie quelle que soit l'adresse.
    const { titre, description } = metadonnees ?? {
      titre: TITRE_INTROUVABLE,
      description: metadonneesDesPages['/'].description,
    }
    // Pas de canonique sur une adresse qui répond 404 : ce serait proposer
    // aux moteurs d'indexer une page inexistante.
    const url = metadonnees ? urlCanonique(pathname) : null

    document.title = titre
    poserMeta('name', 'description', description)
    poserCanonique(url)

    // Open Graph couvre Facebook, WhatsApp, LinkedIn et la plupart des
    // messageries ; les cartes X lisent leurs propres balises. Les deux jeux
    // reprennent le même texte : une page n'a pas deux résumés.
    poserMeta('property', 'og:title', titre)
    poserMeta('property', 'og:description', description)
    poserMeta('property', 'og:url', url)

    poserMeta('name', 'twitter:title', titre)
    poserMeta('name', 'twitter:description', description)
    // `summary_large_image` plutôt que `summary` : l'aperçu occupe alors toute
    // la largeur, ce qui met en valeur la photographie plutôt qu'une vignette.
    poserMeta('name', 'twitter:card', 'summary_large_image')
  }, [pathname])
}
